import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';

import { knowledgeSavedMessage } from '~/components/knowledge/KnowledgeBits';
import {
  type KnowledgeDraft,
  KnowledgeDraftRow,
  newKnowledgeDraft,
  supersedableFor,
} from '~/components/knowledge/KnowledgeDraftRow';
import { type PublishMode, taskSummaryText } from '~/components/tasks/detail/AiSummaryCard';
import { humanLogs } from '~/components/tasks/taskStyle';
import type { KnowledgeEntry, TaskDetail, TaskPublishEntryRequest } from '~/data/generated/model';
import { tasksPublishCreateBodyEntriesItemContentMax } from '~/data/generated/zod/tasks/tasks.zod';
import { usePropertyKnowledge } from '~/data/hooks/useKnowledge';
import { useKnowledgeTargets } from '~/data/hooks/useKnowledgeTargets';
import { useTaskKnowledge } from '~/data/hooks/useTaskKnowledge';
import { BUTTON_SX, TRANSIENT_SURFACE_RADIUS } from '~/theme';

type Props = {
  task: TaskDetail;
  mode: PublishMode;
  onClose: () => void;
};

const TITLES: Record<PublishMode, string> = {
  publish: 'Publish what this task taught us',
  update: 'Update what this task taught us',
  complete: 'Anything the team should remember?',
};

const EYEBROWS: Record<PublishMode, string> = {
  publish: 'PUBLISH TO KNOWLEDGE',
  update: 'UPDATE KNOWLEDGE',
  complete: 'TASK COMPLETE',
};

function eyebrow(task: TaskDetail, mode: PublishMode): string {
  return `${task.displayId} · ${EYEBROWS[mode]}`;
}

function publishEntries(
  drafts: KnowledgeDraft[],
  existing: KnowledgeEntry[],
  propertyId: string,
): TaskPublishEntryRequest[] {
  const superseding = new Set<string>();
  return drafts
    .filter((d) => d.content.trim())
    .map((d) => {
      const older = d.supersede ? supersedableFor(existing, propertyId, d) : undefined;
      const supersedesId = older && !superseding.has(older.id) ? older.id : undefined;
      if (supersedesId) superseding.add(supersedesId);
      return {
        kind: d.kind,
        carId: d.carId,
        content: d.content.trim(),
        ...(supersedesId ? { supersedesId } : {}),
        ...(d.kind === 'commitment' && d.followUp ? { followUpDate: d.followUp } : {}),
      };
    });
}

function PublishForm({ task, mode, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { draftKnowledge, publish } = useTaskKnowledge(task.id);
  const { entries: existing } = usePropertyKnowledge(task.propertyId);
  const { properties } = useKnowledgeTargets();
  const cars = properties.find((p) => p.id === task.propertyId)?.cars ?? task.cars;

  const [fallback] = useState(() =>
    newKnowledgeDraft({
      carId: task.cars.length === 1 ? task.cars[0].id : null,
      content: taskSummaryText(task).slice(0, tasksPublishCreateBodyEntriesItemContentMax),
    }),
  );
  const [drafts, setDrafts] = useState<KnowledgeDraft[]>([]);
  const [drafting, setDrafting] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    draftKnowledge(controller.signal)
      .then((found) => {
        if (cancelled) return;
        setDrafts(
          found.map((d) => newKnowledgeDraft({ kind: d.kind, carId: d.carId, content: d.content })),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setDrafts([fallback]);
      })
      .finally(() => {
        if (!cancelled) setDrafting(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [draftKnowledge, fallback]);

  const entries = publishEntries(drafts, existing, task.propertyId);
  const count = entries.length;

  const setDraft = (key: string, patch: Partial<KnowledgeDraft>) =>
    setDrafts((ds) => ds.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  const addDraft = () => setDrafts((ds) => [...ds, newKnowledgeDraft()]);

  const save = async () => {
    if (!count || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setFailed(false);
    try {
      const result = await publish(entries);
      enqueueSnackbar(
        knowledgeSavedMessage({
          saved: result.created,
          superseded: result.superseded,
          followUps: result.followUpTasks,
        }),
        { variant: 'success' },
      );
      onClose();
    } catch {
      setFailed(true);
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <>
      <Box
        sx={{ px: 3, pt: 2, pr: 6, fontSize: 12, color: 'text.secondary', letterSpacing: '.04em' }}
      >
        {eyebrow(task, mode)}
      </Box>
      <DialogTitle sx={{ pt: 0.5, pb: 0, pr: 6 }}>{TITLES[mode]}</DialogTitle>
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 12, top: 12 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <Box sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>{task.title}</Box>
        <Box
          sx={{
            bgcolor: '#f7f9fd',
            border: '1px solid #dde5f2',
            borderRadius: `${TRANSIENT_SURFACE_RADIUS}px`,
            p: '12px 14px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '.04em',
              color: '#555',
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 16, color: '#1a3f8f' }} />
            AI SUMMARY OF THE WHOLE TASK · {humanLogs(task).length} LOGS
          </Box>
          <Box sx={{ fontSize: 14, lineHeight: 1.5, mt: '6px', color: '#1b1f24' }}>
            {taskSummaryText(task)}
          </Box>
        </Box>

        <Box>
          <Box sx={{ fontSize: 12, fontWeight: 500, letterSpacing: '.06em', color: '#555' }}>
            DRAFT KNOWLEDGE
          </Box>
          <Box sx={{ fontSize: 12, color: '#666', mt: '2px' }}>
            Drafted from the summary. Edit, delete or add before saving.
          </Box>
        </Box>

        {drafting ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: 13,
              color: '#1a3f8f',
              py: '8px',
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 18 }} />
            Drafting from the latest logs…
          </Box>
        ) : null}
        {!drafting && drafts.length === 0 ? (
          <Box
            sx={{
              p: '18px',
              textAlign: 'center',
              color: '#777',
              fontSize: 13,
              border: '1px dashed #d7dbe0',
              borderRadius: `${TRANSIENT_SURFACE_RADIUS}px`,
            }}
          >
            <Box>Nothing in these updates looks worth keeping.</Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addDraft}
              sx={{ ...BUTTON_SX, mt: '8px' }}
            >
              Add an entry
            </Button>
          </Box>
        ) : null}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {drafts.map((d) => (
            <KnowledgeDraftRow
              key={d.key}
              draft={d}
              propertyId={task.propertyId}
              cars={cars}
              existing={existing}
              allowFollowUp
              onChange={(patch) => setDraft(d.key, patch)}
              onRemove={() => setDrafts((ds) => ds.filter((x) => x.key !== d.key))}
            />
          ))}
        </Box>

        {drafts.length > 0 ? (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addDraft}
            sx={{ ...BUTTON_SX, alignSelf: 'flex-start' }}
          >
            Add another
          </Button>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <Box
          component="span"
          sx={{ fontSize: 12, color: failed ? '#b3261e' : '#666', flex: 1, minWidth: 200 }}
        >
          {failed
            ? "Couldn't save to knowledge. Try again."
            : `Saves to ${task.propertyName} · each entry links back to ${task.displayId}`}
        </Box>
        <Button variant="outlined" onClick={onClose} sx={BUTTON_SX}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!count || saving}
          onClick={() => void save()}
          sx={BUTTON_SX}
        >
          {count ? `Save ${count} to knowledge` : 'Save to knowledge'}
        </Button>
      </DialogActions>
    </>
  );
}

export function PublishKnowledgeDialog(props: Props) {
  return (
    <Dialog open onClose={props.onClose} fullWidth maxWidth="sm">
      <PublishForm {...props} />
    </Dialog>
  );
}
